import * as Form from "@radix-ui/react-form"
import { eases } from "library/eases"
import { fmobile, fresponsive, ftablet } from "library/fullyResponsive"
import { generateGradientBorder } from "library/generateGradientBorder"
import styled, { css } from "styled-components"
import colors from "styles/colors"
import media from "styles/media"
import textStyles from "styles/text"

const toSentenceCase = (str: string) =>
	str.toLowerCase().replace(/^\w/, (c) => c.toUpperCase())

export default function TextInput({
	name,
	type = "text",
	required = false,
	placeholder,
	className,
	nameInError,
}: {
	name: string
	type?: "email" | "text" | "textarea" | "tel"
	required?: boolean
	placeholder: string
	className?: string
	nameInError?: string
}) {
	return (
		<Wrapper name={name} className={className}>
			<Control
				asChild
				placeholder={placeholder}
				type={type === "textarea" ? undefined : type}
				required={required}
			>
				{type === "textarea" ? <textarea /> : <input />}
			</Control>

			<FormMessage match="valueMissing">
				{toSentenceCase(nameInError ? nameInError : placeholder)} is required
			</FormMessage>
			{type === "email" && (
				<>
					<FormMessage match="typeMismatch">Enter a valid email</FormMessage>
					{/* forbid dotless domains */}
					<FormMessage match={(v) => !/@.*\./.test(v)}>
						Enter a valid email
					</FormMessage>
				</>
			)}
		</Wrapper>
	)
}

const transition = `0.3s ${eases.quad.inOut}`

const Wrapper = styled(Form.Field)`
	position: relative;
	background: linear-gradient(
		228deg,
		rgb(36 36 36 / 25%) -27%,
		rgb(138 138 138 / 25%) 192.85%
	);
	color: ${colors.blueWhite};
	transition: color ${transition};
	${fresponsive(css`
		width: 100%;
		min-height: 48px;
		border-radius: 25px;
		backdrop-filter: blur(2px);

		textarea {
			min-height: 128px;
		}
	`)}
	${ftablet(css`
		min-height: 60px;
		border-radius: 33px;
	`)}
	${fmobile(css`
		min-height: 60px;
		border-radius: 33px;
	`)}

	&::before, &::after {
		content: "";
		position: absolute;
		inset: 0;
		border-radius: inherit;
		pointer-events: none;
	}

	/* static border */
	&::before {
		${generateGradientBorder(
			"linear-gradient(17deg, #252525 16.5%, #8B8B8B 218.45%)",
			1,
		)}
	}

	/* hover border */
	&::after {
		opacity: 0;
		transition: opacity ${transition};
		${generateGradientBorder(
			"linear-gradient(173deg, #FFF -49.86%, rgba(255, 255, 255, 0.00) 142.67%)",
			1,
		)}
	}

	*::placeholder {
		color: ${colors.gray500};
		transition: color ${transition};
	}

	${media.hover} {
		&:hover {
			&::after {
				opacity: 1;
			}

			*::placeholder {
				color: ${colors.gray400};
			}
		}
	}

	&:focus-within::after {
		opacity: 1;
	}

	&[data-invalid] {
		color: ${colors.gray500};
	}
`

const Control = styled(Form.Control)`
	${textStyles.kicker3};
	width: 100%;
	height: 100%;
	border-radius: inherit;
	cursor: text;
	${fresponsive(css`
		padding: 19px 24px;
	`)}
	${ftablet(css`
		${textStyles.kicker2};
		padding: 22px 24px;
	`)}
	${fmobile(css`
		${textStyles.kicker2};
		padding: 24px;
	`)}

	&:focus {
		outline: none;
	}
`

const FormMessage = styled(Form.Message)`
	position: absolute;
	${textStyles.body2Mini};
	color: #ff5151;
	width: 100%;
	${fresponsive(css`
		top: calc(100% + 2.5px);
		left: 24px;
	`)}
`
